/**
 * How long people spend in each section of the product.
 *
 * A **visit** is one stretch of attention on one `page_section`. Moving between
 * pages of the same section keeps it running; leaving the section ends it and
 * reports `Page Section Exited`. Only visible time accrues, so a backgrounded
 * tab cannot inflate the number.
 *
 * Two things make this less obvious than it looks:
 *
 * 1. **Delivery.** analytics.js puts a `track` through an async queue, so an
 *    event handed over during teardown is never sent — on dev, every `pagehide`
 *    exit was lost while a control request from the same handler went out. So
 *    the visit is reported when the tab goes *hidden*, which is the last moment
 *    the document is reliably alive, and resumed if the user comes back. That
 *    trades one visit for several: sum `time_visible_seconds` per section rather
 *    than reading it per event. `pagehide` remains a backstop, and it only
 *    arrives thanks to `keepalive` on the client (see segment.ts).
 * 2. **Ownership.** The exit names its pages explicitly. A navigation exit is
 *    emitted from the destination's `pageView`, by which time the leaving page
 *    has dropped its context, so the page context would report the wrong page.
 */
import { EventName, PageName, PageSection, PageType } from '@/shared/types/segment';

export type PageSnapshot = {
  page_name: PageName;
  page_section: PageSection;
  page_type: PageType;
  path_template: string;
};

/** An idle tab left open over a weekend would otherwise destroy every average. */
const MAX_SECONDS = 30 * 60;

type Visit = {
  /** Where the visit started. */
  entry: PageSnapshot;
  /** The last page seen inside it, which is the page it exits from. */
  latest: PageSnapshot;
  startedAt: number;
  visibleSince: number | null;
  visibleMs: number;
};

let visit: Visit | null = null;

/**
 * The page the last visit ended on. A tab switch does not re-run `useSegments`,
 * so this is the only way back to a visit after a hidden exit.
 */
let lastSnapshot: PageSnapshot | null = null;

// Monotonic, so a clock change cannot produce a negative or absurd duration.
const now = () => performance.now();

const bankVisibleTime = () => {
  if (visit?.visibleSince != null) {
    visit.visibleMs += now() - visit.visibleSince;
    visit.visibleSince = null;
  }
};

type Track = (name: EventName, props: Record<string, unknown>) => unknown;

/**
 * Ends the open visit and reports how long it lasted. Silent when none is open.
 *
 * @param reason - What ended it; `hidden` is the one that reliably arrives
 * @param track - Injected, not imported, to avoid a cycle with `segment.ts`
 */
export const exitSection = (reason: 'navigation' | 'pagehide' | 'hidden', track: Track) => {
  if (!visit) {
    return;
  }
  bankVisibleTime();

  const elapsed = (now() - visit.startedAt) / 1000;
  const visible = visit.visibleMs / 1000;
  const { entry, latest } = visit;

  // Latch before tracking: StrictMode double-invokes, and a pagehide can follow
  // a visibilitychange within the same tick.
  visit = null;
  lastSnapshot = latest;

  track(EventName.PageSectionExited, {
    page_section: entry.page_section,
    page_type: latest.page_type,
    page_name: latest.page_name,
    path_template: latest.path_template,
    entry_page_name: entry.page_name,
    entry_path_template: entry.path_template,
    time_visible_seconds: Math.round(Math.min(visible, MAX_SECONDS)),
    time_elapsed_seconds: Math.round(Math.min(elapsed, MAX_SECONDS)),
    exit_reason: reason,
    was_capped: elapsed > MAX_SECONDS,
  });
};

const startVisit = (snapshot: PageSnapshot) => {
  visit = {
    entry: snapshot,
    latest: snapshot,
    startedAt: now(),
    visibleSince: document.visibilityState === 'visible' ? now() : null,
    visibleMs: 0,
  };
};

/** Starts a visit. A move within the same section keeps the running one. */
export const enterSection = (snapshot: PageSnapshot, track: Track) => {
  if (visit?.entry.page_section === snapshot.page_section) {
    // The visit continues, but the page it will exit from has moved on.
    visit.latest = snapshot;
    return;
  }
  exitSection('navigation', track);
  startVisit(snapshot);
};

let registered = false;

/**
 * Wires the tab-level listeners. Idempotent: the listeners are global and never
 * removed, so a second call would double every exit event rather than replace
 * the first registration.
 */
export const registerSectionDwell = (track: Track) => {
  if (registered) {
    return;
  }
  registered = true;

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      exitSection('hidden', track);
    } else if (!visit && lastSnapshot) {
      startVisit(lastSnapshot);
    }
  });

  window.addEventListener('pagehide', () => exitSection('pagehide', track));
};

/** Test seam. */
export const resetSectionDwell = () => {
  visit = null;
  lastSnapshot = null;
};
